import React from 'react';

/**
 * Barra de ação de um formulário (at-58, frente C).
 *
 * No mobile ela **gruda no rodapé** (`sticky bottom-0`): o formulário da etapa 1
 * tem 19 campos e, com a barra no fim do fluxo do documento, o consultor precisa
 * rolar até o fundo para descobrir se pode avançar. No desktop volta ao fluxo,
 * alinhada à direita.
 *
 * ⚠️ **`sticky`, não `fixed`.** `fixed` sai do fluxo e passaria por cima do
 * último campo — e o `.main-containt` legado deste app rola no container, não no
 * `<body>`, o que faz `fixed` se ancorar no lugar errado dependendo da tela. Com
 * `sticky` a barra reserva o próprio espaço.
 *
 * ⚠️ **Fundo opaco e borda são obrigatórios**, não enfeite: sobre conteúdo que
 * rola por baixo, uma barra translúcida deixa texto e botão ilegíveis no meio do
 * movimento.
 */

interface PropsFormActions {
  children: React.ReactNode;
  /** Texto de apoio à esquerda (ex.: o que falta preencher). Só no desktop. */
  apoio?: React.ReactNode;
  /**
   * Faixa **abaixo** das ações, dentro da mesma barra — em qualquer largura.
   *
   * 🔴 Existe para o **progresso de uma operação longa acompanhar o botão que a
   * disparou** (at-63): a barra de envio da vistoria ficava no fim do conteúdo e
   * **saía de vista** conforme a grade de 29 fotos crescia, justamente durante o
   * envio, que é quando ela importa (Victor, 10/09/2026: *"deixe essa parte de
   * loading de envio sempre visível na versão mobile, não somente quando desliza
   * até o fim da tela"*).
   *
   * ⚠️ **Com `rodape`, o eixo do desktop muda de linha para COLUNA** — as ações
   * ficam em cima e a faixa embaixo, como no mobile. É o que mantém a barra com a
   * largura toda em vez de espremida ao lado do botão.
   *
   * ⚠️ **Quem não passa `rodape` recebe a MESMA string de classes de antes** (ver
   * a escolha mutuamente exclusiva abaixo) — os 11 consumidores do fluxo de adesão
   * e da cotação não mudam em nada.
   */
  rodape?: React.ReactNode;
  className?: string;
}

const FormActions: React.FC<PropsFormActions> = ({
  children,
  apoio,
  rodape,
  className = '',
}) => (
  <div
    className={[
      'tw-sticky tw-bottom-0 tw-z-10 tw--mx-4 tw-mt-2 tw-flex tw-flex-col tw-gap-3',
      /* ⚠️ Fundo OPACO e borda são obrigatórios, não enfeite: o conteúdo rola por
         baixo desta faixa, e translúcida deixaria o rótulo (e o percentual do
         `rodape`) ilegíveis no meio do movimento. */
      'tw-border-t tw-border-superficie-borda tw-bg-white tw-px-4 tw-py-3',
      'sm:tw-static sm:tw-mx-0 sm:tw-border-0 sm:tw-bg-transparent sm:tw-px-0 sm:tw-py-0',
      /*
       * 🔴 Escolha MUTUAMENTE EXCLUSIVA, nunca as duas listas juntas.
       *
       * `sm:flex-row` e `sm:flex-col` são utilitários de mesma especificidade:
       * emitir os dois faria vencer quem viesse por último no CSS **gerado**, não
       * na string — a armadilha que o `Card.tsx` documenta. Escolhendo aqui, o
       * caso sem `rodape` produz exatamente as classes de antes.
       */
      rodape
        ? 'sm:tw-items-stretch'
        : 'sm:tw-flex-row sm:tw-items-center sm:tw-justify-between',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {apoio ? (
      <div className="tw-hidden tw-text-xs tw-text-texto-suave sm:tw-block">{apoio}</div>
    ) : (
      <span className="tw-hidden sm:tw-block" />
    )}

    <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center">{children}</div>

    {rodape}
  </div>
);

export default FormActions;
